import { useTranslation } from '@/app/providers';
import { Skeleton } from '@/shared/components/skeleton';
import { cn } from '@/shared/lib/cn';

const NAV_PILL_COUNT = 3;
const RECIPE_ROW_COUNT = 3;
const ALLERGEN_COUNT = 12;

function WineRowSkeleton({ wideTitle }: { wideTitle?: boolean }) {
  return (
    <div className="border-b border-white/10 py-5">
      <div className="flex items-start justify-between gap-3">
        <Skeleton
          className={cn(
            'h-4 rounded-md bg-white/20',
            wideTitle ? 'w-52 max-w-[70%]' : 'w-28 max-w-[50%]',
          )}
        />
        <div className="flex shrink-0 items-center gap-2">
          <Skeleton className="h-4 w-12 rounded-md bg-white/20" />
          <Skeleton className="h-5 w-10 rounded-sm bg-white/15" />
        </div>
      </div>
      <Skeleton className="mt-2 h-3 w-full max-w-[18rem] rounded-md bg-white/12" />
      <Skeleton className="mt-1.5 h-3 w-40 max-w-full rounded-md bg-white/10" />
      <div className="mt-3 flex flex-wrap gap-2">
        <Skeleton className="h-6 w-16 rounded-sm bg-white/12" />
        <Skeleton className="h-6 w-24 rounded-sm bg-white/12" />
        <Skeleton className="h-6 w-10 rounded-sm bg-white/12" />
      </div>
    </div>
  );
}

/**
 * Layout-shaped loading state matching the public menu preview shell:
 * full-bleed cover, section nav, wine/text rows, allergens grid.
 * Tailwind only — editorial menu CSS may still be loading.
 */
export function MenuPreviewSkeleton() {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-h-screen w-full justify-center bg-[#0f1729]"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('menu.previewPage.loading')}
    >
      <div className="relative w-full max-w-lg overflow-hidden bg-[#152038] shadow-lg sm:max-w-xl">
        {/* Cover / hero — matches menu-cover-* region */}
        <header className="relative text-center">
          <div className="relative flex min-h-[70vh] w-full flex-col items-center justify-center gap-4 overflow-hidden px-8 py-16 sm:min-h-[75vh]">
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.06)_0%,_transparent_65%)]"
              aria-hidden
            />
            <Skeleton className="absolute left-1/2 top-4 z-20 h-7 w-9 -translate-x-1/2 rounded-sm bg-white/25 sm:top-5" />

            <Skeleton className="relative z-10 h-24 w-36 rounded-md bg-white/20 sm:h-28 sm:w-40" />
            <Skeleton className="relative z-10 h-3 w-14 rounded-md bg-white/25" />
            <Skeleton className="relative z-10 h-9 w-56 max-w-[85%] rounded-md bg-white/30 sm:h-10 sm:w-64" />

            <Skeleton
              variant="circular"
              className="absolute bottom-6 left-1/2 z-10 h-10 w-10 -translate-x-1/2 bg-white/20"
            />
          </div>
        </header>

        {/* Sticky section tabs */}
        <nav className="sticky top-0 z-10 border-b border-white/10 bg-[#152038]/90 px-4 py-3 backdrop-blur-sm">
          <div className="flex gap-2 overflow-hidden">
            {Array.from({ length: NAV_PILL_COUNT }, (_, index) => (
              <Skeleton
                key={index}
                className="h-9 w-20 shrink-0 rounded-md bg-white/15"
              />
            ))}
          </div>
        </nav>

        <main className="px-5 pb-8 pt-8 sm:px-6">
          {/* Section header: SECTION + title + optional PRICE */}
          <div className="mb-2 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-2 text-center">
              <Skeleton className="mx-auto h-3 w-16 rounded-md bg-white/20" />
              <Skeleton className="mx-auto h-7 w-32 rounded-md bg-white/25" />
            </div>
            <div className="shrink-0 space-y-1.5 pt-1 text-center">
              <Skeleton className="mx-auto h-2.5 w-10 rounded-md bg-white/15" />
              <Skeleton className="mx-auto h-4 w-16 rounded-md bg-white/20" />
            </div>
          </div>

          <div className="mt-6 space-y-0">
            {Array.from({ length: RECIPE_ROW_COUNT }, (_, index) => (
              <WineRowSkeleton key={index} wideTitle={index !== 1} />
            ))}
          </div>
        </main>

        {/* Allergens legend */}
        <section className="mx-4 mb-6 rounded-sm border border-white/10 px-4 py-6 sm:mx-5">
          <Skeleton className="mx-auto mb-5 h-4 w-28 rounded-md bg-white/25" />
          <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:grid-cols-4">
            {Array.from({ length: ALLERGEN_COUNT }, (_, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-2 text-center"
              >
                <Skeleton
                  variant="circular"
                  className="h-10 w-10 bg-white/15"
                />
                <Skeleton className="h-2.5 w-14 rounded-md bg-white/12" />
              </div>
            ))}
          </div>
        </section>

        <div className="pb-8 pt-2 text-center">
          <Skeleton className="mx-auto h-3 w-36 rounded-md bg-white/15" />
        </div>
      </div>
      <span className="sr-only">{t('menu.previewPage.loading')}</span>
    </div>
  );
}
